import React from 'react';

import { Alert, AlertTitle } from '@mui/material';
import { DateTime } from 'luxon';
import { useForm } from 'react-final-form';
import { useDebounce } from 'use-debounce';

import { SUPPORT_CLEARVERIFY_EMAIL } from '@clearverify/constants';

import { useCheckEmployeeQuery } from 'src/api';
import { VerificationRequestCreateFormFields } from 'src/constants/verificationRequest';
import { useCurrentUserPermissions } from 'src/hooks/user';
import { useVerificationRequestFormData } from 'src/hooks/verificationRequest';

import { VerificationRequestCreateFormValues } from './types';

export const VerificationRequestCreateByVerifierModalFormConsumerErrorBox: React.FC = () => {
  const { getState } = useForm<VerificationRequestCreateFormValues>();
  const { values, errors = {} } = getState();

  const { hasPlatformRoles } = useCurrentUserPermissions();

  const { setIsEmployeeInfoLoading, setIsEmployeeExists, setIsEmployeeFrozen } =
    useVerificationRequestFormData();

  const isFieldsValid = React.useMemo(() => {
    return (
      !(VerificationRequestCreateFormFields.ssn in errors) &&
      !(VerificationRequestCreateFormFields.employeeDateOfBirth in errors)
    );
  }, [errors]);

  const [{ ssn, employeeDateOfBirth }] = useDebounce(values, DEBOUNCE_DELAY);

  const employeeDateOfBirthDateTime = React.useMemo(() => {
    return employeeDateOfBirth ? DateTime.fromISO(employeeDateOfBirth) : undefined;
  }, [employeeDateOfBirth]);

  const { data, loading } = useCheckEmployeeQuery({
    variables: {
      ssn,
      employeeBirthDate: employeeDateOfBirthDateTime?.isValid
        ? employeeDateOfBirthDateTime.toISODate()
        : '',
    },
    skip: !isFieldsValid || !ssn || !employeeDateOfBirthDateTime?.isValid,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      setIsEmployeeExists(!!data.checkEmployee);
      setIsEmployeeFrozen(!!data.checkEmployee?.isEmployeeFrozen);
    },
    onError: () => {
      setIsEmployeeExists(false);
      setIsEmployeeFrozen(false);
    },
  });

  React.useEffect(() => {
    setIsEmployeeInfoLoading(loading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const isEmployeeFound = React.useMemo(() => {
    return !loading && !!data?.checkEmployee;
  }, [data?.checkEmployee, loading]);

  if (data && !isEmployeeFound) {
    return (
      <Alert sx={{ mt: 2 }} severity="info">
        <AlertTitle>Attention</AlertTitle>
        {hasPlatformRoles
          ? alertCommonPart
          : `${alertCommonPart} Contact our support team for assistance if you believe this is an error (${SUPPORT_CLEARVERIFY_EMAIL}).`}
      </Alert>
    );
  }

  return null;
};

const alertCommonPart =
  "We can't find this employee. Please confirm all of the employee details are correct. If they are, we may not have this employee in our system.";

const DEBOUNCE_DELAY = 1000;
